import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {AlertsService} from '../../services/alertsService';
import {Alert, AlertsState, CreateAlertRequest} from '../../types';

// Initial state
const initialState: AlertsState = {
  alerts: [],
  selectedAlert: null,
  isLoading: false,
  isDetailsLoading: false,
  error: null,
};

// Async thunks for alert operations
export const fetchAlerts = createAsyncThunk(
  'alerts/fetchAlerts',
  async (_, { rejectWithValue }) => {
    try {
         return await AlertsService.getAlerts();
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch alerts');
    }
  }
);

export const fetchAlertById = createAsyncThunk(
  'alerts/fetchAlertById',
  async (alertId: string, { rejectWithValue }) => {
    try {
        return await AlertsService.getAlertById(alertId);


    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch alert');
    }
  }
);

export const fetchAlertsByClient = createAsyncThunk(
  'alerts/fetchAlertsByClient',
  async (clientId: string, { rejectWithValue }) => {
    try {
        return await AlertsService.getAlertsByClient(clientId);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch alerts for client');
    }
  }
);

export const createAlert = createAsyncThunk(
  'alerts/createAlert',
  async (alertData: any, { rejectWithValue }) => {
    try {
         return await AlertsService.createAlert(alertData);

    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create alert');
    }
  }
);

export const updateAlert = createAsyncThunk(
  'alerts/updateAlert',
  async ({ alertId, alertData }: { alertId: string; alertData: Partial<any> }, { rejectWithValue }) => {
    try {
        return await AlertsService.updateAlert(alertId, alertData);


    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update alert');
    }
  }
);

export const deleteAlert = createAsyncThunk(
  'alerts/deleteAlert',
  async (alertId: string, { rejectWithValue }) => {
    try {
      await AlertsService.deleteAlert(alertId);
      // Backend returns success message, return alertId for state management
      return alertId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete alert');
    }
  }
);

// Slice
const alertsSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    clearSelectedAlert: (state) => {
      state.selectedAlert = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedAlert: (state, action: PayloadAction<Alert>) => {
      state.selectedAlert = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch all alerts
    builder.addCase(fetchAlerts.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchAlerts.fulfilled, (state, action) => {
      state.isLoading = false;
      state.alerts = action.payload;
    });
    builder.addCase(fetchAlerts.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Fetch alert by ID
    builder.addCase(fetchAlertById.pending, (state) => {
      state.isDetailsLoading = true;
      state.error = null;
    });
    builder.addCase(fetchAlertById.fulfilled, (state, action) => {
      state.isDetailsLoading = false;
      state.selectedAlert = action.payload;
    });
    builder.addCase(fetchAlertById.rejected, (state, action) => {
      state.isDetailsLoading = false;
      state.error = action.payload as string;
    });

    // Fetch alerts by client
    builder.addCase(fetchAlertsByClient.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchAlertsByClient.fulfilled, (state, action) => {
      state.isLoading = false;
      state.alerts = action.payload;
    });
    builder.addCase(fetchAlertsByClient.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Create alert
    builder.addCase(createAlert.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createAlert.fulfilled, (state, action) => {
      state.isLoading = false;
      state.alerts.push(action.payload);
    });
    builder.addCase(createAlert.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Update alert
    builder.addCase(updateAlert.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateAlert.fulfilled, (state, action) => {
      state.isLoading = false;
      const index = state.alerts.findIndex((alert) => alert.id === action.payload.id);
      if (index !== -1) {
        state.alerts[index] = action.payload;
      }
      if (state.selectedAlert?.id === action.payload.id) {
        state.selectedAlert = action.payload;
      }
    });
    builder.addCase(updateAlert.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Delete alert
    builder.addCase(deleteAlert.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteAlert.fulfilled, (state, action) => {
      state.isLoading = false;
      state.alerts = state.alerts.filter((alert) => alert.id !== action.payload);
      if (state.selectedAlert?.id === action.payload) {
        state.selectedAlert = null;
      }
    });
    builder.addCase(deleteAlert.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearSelectedAlert, clearError, setSelectedAlert } = alertsSlice.actions;
export default alertsSlice.reducer;
